import { useState, useEffect } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Mail, Eye, EyeOff } from "lucide-react";

const TARGET_OPTIONS = [
  { value: "all", label: "Everyone" },
  { value: "qualified", label: "Qualified Clients" },
  { value: "clients", label: "All Clients" },
  { value: "referrers", label: "Referrers" },
  { value: "admins", label: "Admins & Superusers" },
];

const PRIORITIES = ["info", "success", "warning", "critical"];

const AnnouncementsAdminPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", target_roles: ["qualified"], priority: "info", send_email: false });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/announcements");
      setItems(r.data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const toggleTarget = (v) => {
    setForm((p) => {
      if (v === "all") return { ...p, target_roles: ["all"] };
      const without_all = p.target_roles.filter((r) => r !== "all");
      return {
        ...p,
        target_roles: without_all.includes(v)
          ? without_all.filter((r) => r !== v)
          : [...without_all, v],
      };
    });
  };

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body required"); return; }
    try {
      await api.post("/announcements", form);
      toast.success("Announcement posted");
      setOpen(false);
      setForm({ title: "", body: "", target_roles: ["qualified"], priority: "info", send_email: false });
      load();
    } catch { toast.error("Failed to create"); }
  };

  const togglePublish = async (a) => {
    try {
      await api.patch(`/announcements/${a.id}`, { published: !a.published });
      toast.success(a.published ? "Unpublished" : "Published");
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  const priorityColor = (p) => ({
    info: "bg-blue-500/20 text-blue-300 border-blue-500/30",
    success: "bg-emerald/20 text-emerald border-emerald/30",
    warning: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
    critical: "bg-red-500/20 text-red-400 border-red-500/30",
  }[p] || "");

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="announcements-admin-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-serif text-foreground flex items-center gap-3">
              <Megaphone className="text-gold" /> Broadcast Announcements
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Send updates to clients, referrers, or the whole platform.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-announcement-btn"><Plus size={16} className="mr-1" /> New Announcement</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl" data-testid="announcement-dialog">
              <DialogHeader><DialogTitle>Create Announcement</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="announcement-title" />
                </div>
                <div>
                  <Label>Body</Label>
                  <Textarea rows={6} value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} data-testid="announcement-body" />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm((p) => ({ ...p, priority: v }))}>
                    <SelectTrigger data-testid="announcement-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (<SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target Audience</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {TARGET_OPTIONS.map((t) => (
                      <label key={t.value} className="flex items-center gap-2 cursor-pointer border border-platinum/10 rounded p-2 hover:bg-platinum/5">
                        <Checkbox
                          checked={form.target_roles.includes(t.value)}
                          onCheckedChange={() => toggleTarget(t.value)}
                          data-testid={`target-${t.value}`}
                        />
                        <span className="text-sm">{t.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.send_email}
                    onCheckedChange={(v) => setForm((p) => ({ ...p, send_email: !!v }))}
                    data-testid="send-email-checkbox"
                  />
                  <Mail size={14} />
                  <span className="text-sm">Also email matching users</span>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} data-testid="submit-announcement">Post</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <Card className="bg-surface border-platinum/10">
            <CardContent className="p-8 text-center text-muted-foreground">No announcements yet</CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((a) => (
              <Card key={a.id} className="bg-surface border-platinum/10" data-testid={`announcement-row-${a.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="text-lg font-medium text-foreground">{a.title}</h3>
                        <Badge className={priorityColor(a.priority)}>{a.priority.toUpperCase()}</Badge>
                        {!a.published && <Badge variant="outline" className="text-muted-foreground">HIDDEN</Badge>}
                        {a.send_email && <Badge className="bg-gold/20 text-gold border-gold/30">EMAILED · {a.email_recipients_count}</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap mb-2">{a.body}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        <span>By {a.created_by_name || "Admin"}</span>
                        <span>·</span>
                        <span>{new Date(a.created_at).toLocaleString()}</span>
                        <span>·</span>
                        <span>Targets: {a.target_roles.join(", ")}</span>
                        <span>·</span>
                        <span>{a.dismissed_by.length} dismissed</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => togglePublish(a)} data-testid={`toggle-publish-${a.id}`}>
                        {a.published ? <EyeOff size={14} /> : <Eye size={14} />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => remove(a.id)} data-testid={`delete-announcement-${a.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnnouncementsAdminPage;
